/**
 * What the opposition is made of.
 *
 * Split out of the run for two reasons. The run is long enough already, and
 * the only honest way to check fifteen silhouettes is to look at all fifteen
 * side by side — which means something other than the run has to be able to
 * build one.
 *
 * Six boxes each. Programmer art is an advantage here: a tall thin thing, a
 * squat wide thing and a small floating thing are told apart at a glance and
 * across a dark room, which is more than can be said for five humanoids in
 * different jackets.
 */

import * as THREE from 'three';
import type { EnemyBuild, EnemyType } from './enemies.ts';
import type { Tier } from './quality.ts';

/** The parts every enemy is made of, whatever it happens to be. */
export interface Body {
  group: THREE.Group;
  body: THREE.Mesh;
  head: THREE.Mesh;
  /** Two legs for walkers, two rotors for floaters. Animated by `stride`. */
  limbs: [THREE.Mesh, THREE.Mesh];
  /** Two arms, or fins, or shield plates depending on the build. */
  arms: [THREE.Mesh, THREE.Mesh];
  /** The one bright thing on the body, so you can tell where its front is. */
  eye: THREE.Mesh;
  /**
   * What it does to you, as a shape.
   *
   * Colour is not enough — a third of players cannot separate the red one from
   * the green one, and nobody can at forty metres in fog. So the role gets an
   * outline of its own: a long barrel on anything that owns a sightline, a
   * halo on anything that makes the others harder, fins on anything that
   * denies ground. You should be able to name the threat from the silhouette
   * before you can read a single pixel of the body.
   */
  mark: THREE.Mesh;
  /** What it is about to do, drawn where you can see it. */
  tell: THREE.Mesh;
  type: EnemyType;
  age: number;
}

/** A thin bar drawn between a marksman and where it is about to shoot. */
export const TELL_BEAM = new THREE.BoxGeometry(1, 1, 1);
/** A disc on the ground where a shell is about to land. */
export const TELL_RING = new THREE.CircleGeometry(1, 20);

/**
 * The shell of a hostile thing, and it is not a box.
 *
 * A twenty-sided solid at radius one half, so it scales exactly like the unit
 * cube it replaces and every proportion the silhouette work established still
 * holds. Flat-shaded it reads as cut and faceted rather than moulded, which is
 * the whole point: these are the only crystalline objects in a world of flat
 * architectural planes, and being obviously made of a different substance is
 * how they stay legible against it at forty metres.
 */
export const PRISM = new THREE.IcosahedronGeometry(0.5, 0);
/** Graphite appendages stay boxes: machined parts, bolted onto a crystal. */
const LIMB = new THREE.BoxGeometry(1, 1, 1);

/** Hostile. The one hue in the game reserved for things trying to kill you. */
const CORE = 0xff3158;
/** The shell under the iridescence: nearly black, faintly warm. */
const SHELL = 0x2a1620;
/** Bolted-on parts are the same graphite as every door and machine. */
const ATTACHMENT = 0x171d24;

/**
 * The shell material, at whatever fidelity this machine can pay for.
 *
 * `MeshPhysicalMaterial` gives real thin-film iridescence and a clearcoat, and
 * costs noticeably more per pixel than the standard material — which is fine
 * for a dozen enemies and would not be for a world. Below the top tier the
 * same read is approximated with a flat-shaded dark shell and a strong
 * environment response: it is not the same effect, but it is the same
 * *category* of object, which is what has to survive.
 */
function shellMaterial(tier: Tier): THREE.Material {
  if (tier === 'high') {
    return new THREE.MeshPhysicalMaterial({
      color: SHELL,
      roughness: 0.08,
      metalness: 0.12,
      clearcoat: 1,
      clearcoatRoughness: 0.08,
      iridescence: 1,
      iridescenceIOR: 1.35,
      iridescenceThicknessRange: [180, 520],
      flatShading: true,
      emissive: new THREE.Color(CORE),
      emissiveIntensity: 0.05,
    });
  }
  return new THREE.MeshStandardMaterial({
    color: SHELL,
    roughness: 0.14,
    metalness: 0.55,
    envMapIntensity: 1.6,
    flatShading: true,
    emissive: new THREE.Color(CORE),
    emissiveIntensity: 0.05,
  });
}

export function makeBody(type: EnemyType, tier: Tier = 'balanced'): Body {
  const group = new THREE.Group();
  const shell = (): THREE.Mesh => new THREE.Mesh(PRISM, shellMaterial(tier));
  const part = (): THREE.Mesh =>
    new THREE.Mesh(LIMB, new THREE.MeshStandardMaterial({
      color: ATTACHMENT, roughness: 0.34, metalness: 0.6, envMapIntensity: 0.8, flatShading: true,
    }));
  const body = shell();
  const head = shell();
  const limbMat = new THREE.MeshStandardMaterial({
    color: ATTACHMENT, roughness: 0.34, metalness: 0.6, envMapIntensity: 0.8, flatShading: true,
  });
  const limbs: [THREE.Mesh, THREE.Mesh] = [new THREE.Mesh(LIMB, limbMat), new THREE.Mesh(LIMB, limbMat)];
  const armMat = new THREE.MeshStandardMaterial({
    color: ATTACHMENT, roughness: 0.34, metalness: 0.6, envMapIntensity: 0.8, flatShading: true,
  });
  const arms: [THREE.Mesh, THREE.Mesh] = [new THREE.Mesh(LIMB, armMat), new THREE.Mesh(LIMB, armMat)];
  /*
   * The core.
   *
   * Unlit and above the tone map, so it is a light rather than a bright paint:
   * this is the only thing on the model whose colour never shifts with the
   * angle you see it from, which is what makes a hostile readable at any
   * distance however prismatic its shell is being.
   */
  const eye = new THREE.Mesh(PRISM, new THREE.MeshBasicMaterial({ color: CORE, toneMapped: false }));
  const mark = part();
  const tell = new THREE.Mesh(
    TELL_BEAM,
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, fog: false }),
  );
  tell.visible = false;
  tell.renderOrder = 3;
  group.add(body, head, ...limbs, ...arms, eye, mark);
  const made: Body = { group, body, head, limbs, arms, eye, mark, tell, type, age: 0 };
  shapeBody(made, type);
  return made;
}

/**
 * How solid the body is drawn, 0 to 1.
 *
 * Used for the second an enemy has arrived but has not started moving: it
 * fades up out of nothing where its spawn light is, so an arrival is
 * something you watch happen rather than something that is suddenly beside
 * you. Every body owns its own materials, so this is per enemy.
 *
 * `depthWrite` stays on. A fading solid that stops writing depth shows you its
 * own far side through its near side, which reads as a broken model rather
 * than as a faint one.
 */
export function fade(e: Body, alpha: number): void {
  const solid = alpha >= 0.999;
  for (const m of [e.body, e.head, e.limbs[0], e.limbs[1], e.arms[0], e.arms[1], e.eye, e.mark]) {
    const mat = m.material as THREE.Material;
    mat.transparent = !solid;
    mat.opacity = alpha;
  }
}

/**
 * How big the head is drawn, by build.
 *
 * Exported because `run.ts` decides headshots with it. It used to call the top
 * 42cm of any enemy a head no matter what was up there, which on an orb — a
 * ball with a head scaled to a thousandth of a metre — was most of the
 * creature, and on a walker took in the shoulders. One number, one meaning.
 */
export function headScale(b: EnemyBuild): number {
  return b === 'hulk' ? 0.5 : b === 'crawler' ? 0.28 : b === 'orb' ? 0.001 : b === 'turret' ? 0.44 : 0.34;
}

/** The band at the top of an enemy that counts as its head. */
export function headDepth(t: EnemyType): number {
  const size = headScale(t.build);
  // The shell is a unit cube scaled by `size` and hung so its middle sits a
  // little below the crown, so the band worth calling a head is about that
  // deep. Anything without a head to speak of gets none.
  return size < 0.05 ? 0 : Math.min(t.height * 0.35, size * 1.1);
}

/**
 * Silhouette is the whole job.
 *
 * Six boxes each, but proportioned so the shape alone answers "what is that
 * and how fast is it coming": legs under a walker so stride reads as speed
 * across a dark room, rotors on the floater so it is obviously not standing
 * on anything, a head that is small on a runner and huge on a brute.
 */
export function shapeBody(e: Body, t: EnemyType): void {
  e.type = t;
  e.age = 0;
  // A marksman draws a line down the lane; a mortar draws a circle on the
  // ground. Two shared geometries, swapped rather than rebuilt.
  e.tell.geometry = t.lobs ? TELL_RING : TELL_BEAM;
  e.tell.visible = false;
  (e.tell.material as THREE.MeshBasicMaterial).color.setHex(0xff3158);
  const floats = t.hover !== undefined;
  const b = t.build;
  // A turret is a head on a tripod: no torso to speak of, all of it legs.
  const legH = floats || b === 'orb' ? 0 : b === 'turret' ? t.height * 0.55 : b === 'crawler' ? t.height * 0.22 : Math.min(0.6, t.height * 0.32);
  const bodyH = Math.max(0.2, t.height - (b === 'orb' ? 0 : 0.34) - legH);

  // Torso proportions carry the read: a crawler is long and low, a stalker
  // is narrow and tall, a hulk is nearly as wide as it is tall.
  const wide = b === 'hulk' ? 1.0 : b === 'crawler' ? 0.9 : b === 'stalker' ? 0.62 : b === 'orb' ? 1.0 : b === 'turret' ? 0.85 : 0.8;
  const deep = b === 'crawler' ? 1.35 : b === 'drone' ? 1.1 : b === 'orb' ? 1.0 : b === 'turret' ? 0.85 : 0.78;
  e.body.scale.set(t.radius * 2 * wide, bodyH, t.radius * 2 * deep);
  e.body.position.set(0, legH + bodyH / 2, 0);
  /*
   * No per-class colour any more.
   *
   * Every type used to carry its own body and head hue, so a fight was six or
   * seven saturated colours moving about — and none of them meant anything a
   * player could name, because "the green one" is not a role. Role is carried
   * by silhouette, which is what the proportions below are for, and colour is
   * carried by one thing: hostile is coral, and everything hostile is coral.
   */

  const headSize = headScale(b);
  e.head.scale.setScalar(headSize);
  e.head.position.set(0, t.height - headSize * 0.55, b === 'crawler' ? t.radius * 0.5 : 0);
  const hm = e.head.material as THREE.MeshStandardMaterial;
  hm.emissiveIntensity = 0;

  // A single bright spot on the front. Cheap, and it is the difference
  // between "a box" and "something looking at you".
  const eyeSize = b === 'orb' ? 0.3 : 0.13;
  e.eye.scale.set(eyeSize * (b === 'hulk' ? 2.2 : 1.6), eyeSize, eyeSize * 0.5);
  e.eye.position.set(0, b === 'orb' ? t.height * 0.55 : t.height - headSize * 0.55, -(b === 'orb' ? t.radius : headSize * 0.5 + 0.02) - 0.02);
  // Bigger on the things that hit hardest: the core is the health bar.


  for (const [i, limb] of e.limbs.entries()) {
    const side = i === 0 ? -1 : 1;
    limb.visible = legH > 0.01 || floats;
    if (floats) {
      limb.scale.set(t.radius * 1.6, 0.06, t.radius * 0.45);
      limb.position.set(side * t.radius * 1.2, t.height * 0.58, 0);
    } else if (b === 'turret') {
      // Splayed, so it reads as something set down rather than something
      // standing up — you should be able to tell it will not follow you.
      limb.scale.set(t.radius * 0.22, legH, t.radius * 0.22);
      limb.position.set(side * t.radius * 0.6, legH / 2, t.radius * 0.35);
      limb.rotation.z = -side * 0.28;
    } else {
      limb.rotation.z = 0;
      limb.scale.set(t.radius * 0.6, legH, t.radius * 0.6);
      limb.position.set(side * t.radius * 0.52, legH / 2, 0);
    }
  }

  // A shield plate is the one attachment that reads lighter, because it is
  // the one you are meant to notice and go around.
  const am = e.arms[0].material as THREE.MeshStandardMaterial;
  am.color.setHex(t.frontArmour ? 0x39424c : 0x171d24);
  for (const [i, arm] of e.arms.entries()) {
    const side = i === 0 ? -1 : 1;
    if (t.frontArmour) {
      // A shield reads as a slab across the front, and it is also the thing
      // you are being asked to get around.
      arm.visible = true;
      arm.scale.set(t.radius * 1.3, bodyH * 0.85, 0.16);
      arm.position.set(side * t.radius * 0.55, legH + bodyH * 0.55, -t.radius * deep - 0.1);
    } else if (b === 'orb' || b === 'drone') {
      arm.visible = false;
    } else {
      const armLen = bodyH * (b === 'hulk' ? 0.8 : 0.66);
      arm.visible = true;
      arm.scale.set(t.radius * 0.42, armLen, t.radius * 0.42);
      arm.position.set(side * (t.radius * wide + t.radius * 0.28), legH + bodyH * 0.62, 0);
    }
  }

  /*
   * The role, as an outline.
   *
   * One extra box, placed and proportioned by what the enemy does rather than
   * by what it is built from — so two things with the same body plan and
   * different jobs still read differently, which is the case that matters:
   * a lancer and a grunt are both bipeds.
   */
  const mm = e.mark.material as THREE.MeshStandardMaterial;
  /*
   * The role mark is graphite, and it glows coral when it is about to matter.
   *
   * Shape says which role — a lance, a halo, fins, a slab — and the emissive
   * says when. Colouring the mark per class said neither, twice.
   */
  mm.color.setHex(0x1f262e);
  mm.emissive.setHex(0xff3158);
  mm.emissiveIntensity = 0.18;
  e.mark.visible = true;
  e.mark.rotation.set(0, 0, 0);
  const chest = legH + bodyH * 0.6;
  switch (t.pressure) {
    case 'suppress':
      // A long barrel out front. Anything with one of these owns the lane it
      // is pointing down, and that is the only thing you need to know.
      e.mark.scale.set(t.radius * 0.22, t.radius * 0.22, t.height * 0.62);
      e.mark.position.set(t.radius * 0.5, chest, -(t.radius * deep + t.height * 0.3));
      break;
    case 'support':
      // A halo. It is not shooting at you and killing it first is the answer.
      e.mark.scale.set(t.radius * 2.4, 0.07, t.radius * 2.4);
      e.mark.position.set(0, t.height + 0.28, 0);
      break;
    case 'deny':
      // Fins, angled down: it is above you and what it drops is the problem.
      e.mark.scale.set(t.radius * 3.0, 0.09, t.radius * 0.7);
      e.mark.position.set(0, legH + bodyH * 0.9, t.radius * 0.4);
      e.mark.rotation.x = 0.45;
      break;
    case 'evade':
      // A swept plate along the back: it is going to move, and quickly.
      e.mark.scale.set(t.radius * 0.5, bodyH * 0.75, t.radius * 0.5);
      e.mark.position.set(0, chest, t.radius * deep + 0.16);
      e.mark.rotation.x = -0.5;
      break;
    default:
      e.mark.visible = false;
  }
}
